import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { I18nModule, PaginationModel, TranslationService } from '@spartacus/core';
import { TableProConfig } from './core/table-pro.model';
import { Option } from '../../core/models/option.model';
import { CustomPaginationModule } from '../../spartacus/features/misc/pagination/pagination.module';
import { tablePro } from '../../../assets/translations/en/translations/tablePro';

enum DIRECTION {
  UP = 'up',
  DOWN = 'down',
  NONE = '',
}

@Component({
  selector: 'table-pro',
  standalone: true,
  imports: [CommonModule, I18nModule, ReactiveFormsModule, CustomPaginationModule],
  templateUrl: './table-pro.component.html',
  styleUrl: './table-pro.component.scss',
})
export class TableProComponent implements OnInit, AfterViewInit, OnDestroy {
  DIRECTION = DIRECTION;

  @ViewChild('container', { static: true }) containerRef!: ElementRef;
  @ViewChild('ordenableTool', { static: true }) ordenableToolRef!: ElementRef;
  @HostBinding('class') hostClass = '';
  @Input() configs?: TableProConfig;

  suffixFormattedCollunm: string = '_formatted_value';
  activeElementsClass = 'text-accent-1';

  subscription = new Subscription();
  allData$ = new BehaviorSubject<any[]>([]);
  paginatedData$ = new BehaviorSubject<any[][]>([]);
  displayData$ = new BehaviorSubject<any[]>([]);
  allRows$: NodeListOf<Element> | undefined = undefined;

  currentCollumn = 0;
  currentDirection = DIRECTION.NONE;

  currentOrder?: {
    columnIndex: number;
    direction: DIRECTION;
  };

  defaultConfigs: TableProConfig = {
    classContainer: tablePro.tablePro.classContainer,

    currentPage: 0,
    itemsPerPage: 5,
    itemsPerPageConfig: [5, 10, 15, 20, 50],

    hasItemsPerPageSelector: true,
    hasSearchBox: true,
    noContentMessage: tablePro.tablePro.noContentMessage,
    floattingBottom: false,
    inputSearch: {
      placeholder: tablePro.tablePro.inputSearch.placeholder,
    },
  };

  qtyOptions: Option[] = [];

  pagination?: PaginationModel = {
    currentPage: 0,
    pageSize: this.defaultConfigs?.itemsPerPage,
    sort: '',
    totalPages: 0,
    totalResults: 0,
  };

  form: FormGroup = this.fb.group({
    search: ['', []],
    itemsPerPage: [this.defaultConfigs.itemsPerPage, []],
  });

  constructor(
    protected fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    this.setConfigs();
    this.handlerSearch();
    this.handlerItemsPerPage();
  }

  ngAfterViewInit(): void {
    this.getData();
    this.form.get('search')?.setValue(this.configs?.currentValue || '');
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  setConfigs() {
    this.configs = {
      ...this.defaultConfigs,
      ...this.configs,
    };

    this.qtyOptions = this.configs.itemsPerPageConfig!.map(qty => ({
      code: qty.toString(),
      label: tablePro.tablePro.itemsPerPage.replace('{{count}}', qty.toString()),
    }));

    this.hostClass = this.configs.classContainer || '';
    this.form.get('itemsPerPage')?.setValue(this.configs?.itemsPerPage || '');
  }

  handlerSearch() {
    this.subscription.add(
      this.form.get('search')?.valueChanges.subscribe(value => {
        this.filterData(value);
      }),
    );
  }

  handlerItemsPerPage() {
    this.subscription.add(
      this.form.get('itemsPerPage')?.valueChanges.subscribe(value => {
        this.configs!.itemsPerPage = parseInt(value);

        this.pagination!.currentPage = 0;
        this.pagination!.pageSize = this.configs!.itemsPerPage;

        this.filterData(this.form.get('search')?.value);
      }),
    );
  }

  getData() {
    const $container = this.containerRef.nativeElement as HTMLDivElement;
    const $table = $container.querySelector('table') as HTMLTableElement;
    this.allData$.next(this.tableToArray($table));

    this.paginateData(this.allData$.value);
    this.setOrdenables();
  }

  tableToArray(table: HTMLTableElement): Array<Record<string, any>> {
    const result: Array<Record<string, any>> = [];

    const headers: string[] = Array.from(table.querySelectorAll('thead th')).map(
      header => header.textContent?.trim() || '',
    );

    const rows = table.querySelectorAll('tbody tr');
    this.allRows$ = rows;

    rows.forEach((row, originIndex) => {
      const cells = row.querySelectorAll('td');
      const rowObject: Record<string, any> = {};

      cells.forEach((cell, index) => {
        const header = headers[index] || `column${index + 1}`;
        rowObject[header] = cell.getAttribute('data-value') || cell.textContent?.trim() || '';
      });

      cells.forEach((cell, index) => {
        const header = headers[index] || `column${index + 1}`;
        rowObject[header + this.suffixFormattedCollunm] = cell.textContent?.trim() || '';
      });

      rowObject['originIndex'] = originIndex;
      result.push(rowObject);
    });

    return result;
  }

  filterData(search: string) {
    const normalizeVAlue = (value: string) => {
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLocaleLowerCase();
    };

    const _search = normalizeVAlue(search);

    const allData = this.allData$.value;

    if (!search) {
      this.displayData$.next(allData);
      this.paginateData(this.displayData$.value);

      if (this.currentDirection !== DIRECTION.NONE) {
        this.ordem(this.currentCollumn, this.currentDirection);
      }

      return;
    }

    const displayData = allData.filter(row =>
      Object.values(row).some((value: any) => normalizeVAlue(value.toString()).includes(_search)),
    );

    this.displayData$.next(displayData);
    this.paginateData(this.displayData$.value);

    if (this.currentDirection !== DIRECTION.NONE) {
      this.ordem(this.currentCollumn, this.currentDirection);
    }
  }

  paginateData(data: Record<string, any>) {
    const allData = data;
    const qty = this.configs!.itemsPerPage!;
    const pages = [];

    for (let i = 0; i < allData['length']; i += qty) {
      pages.push(allData['slice'](i, i + qty));
    }
    this.paginatedData$.next(pages);

    this.setPagination({
      currentPage: this.configs!.currentPage,
      pageSize: qty,
      totalPages: pages['length'],
      totalResults: allData['length'],
    });
  }

  setPagination(newPagnaition: Partial<PaginationModel>) {
    this.pagination = {
      ...this.pagination,
      ...newPagnaition,
    };

    this.displayData$.next(this.paginatedData$.value[this.pagination.currentPage || 0]);
    this.factoryRows(this.displayData$.value);
    this.cdr.detectChanges();
  }

  pageChange(currentPage: number) {
    this.setPagination({
      currentPage,
    });
  }

  factoryRows(displayData: Record<string, any>) {
    const $container = this.containerRef.nativeElement as HTMLDivElement;
    const $table = $container.querySelector('table') as HTMLTableElement;
    const rows: Element[] = [];

    (displayData || [])['forEach']((data: any) => {
      const index = data.originIndex;
      const row = this.allRows$?.[index];
      rows.push(row!);
    });

    const tbody = $table.querySelector('tbody');

    if (tbody) {
      tbody.innerHTML = '';

      if (!rows.length) {
        const emptyRow = document.createElement('tr');
        const col = document.createElement('td');
        col.setAttribute('colspan', '100%');
        col.classList.add('text-center');

        let message = this.configs?.noContentMessage || '';

        if (this.form.get('search')?.value) {
          message += ` for <strong>"${this.form.get('search')?.value}"</strong>`;
        }

        col.innerHTML = message;

        emptyRow.appendChild(col);
        tbody.appendChild(emptyRow);
      }

      rows.forEach(row => {
        tbody.appendChild(row);
      });
    }
  }

  setOrdenables() {
    const $container = this.containerRef.nativeElement as HTMLDivElement;
    const $table = $container.querySelector('table') as HTMLTableElement;
    const $ordenableTool = this.ordenableToolRef.nativeElement as HTMLDivElement;
    const headersColumns = $table.querySelectorAll('thead th') as NodeListOf<HTMLTableCellElement>;

    headersColumns.forEach((column, index) => {
      const isOrdenable = this.configs?.collumnConfig?.[index]?.isOrdenable ?? true;

      if (isOrdenable) {
        const $ordenableToolClone = $ordenableTool.cloneNode(true) as HTMLDivElement;
        $ordenableToolClone.classList.remove('d-none');

        const cleanAllOrders = () => {
          const $container = this.containerRef.nativeElement as HTMLDivElement;
          const $allButtons = $container.querySelectorAll('thead th button') as NodeListOf<HTMLButtonElement>;

          $allButtons.forEach(elButton => {
            elButton.classList.remove(this.activeElementsClass);
            elButton.querySelector('span')!.innerHTML = 'swap_vert';
          });
        };

        const getDirection = (): DIRECTION => {
          if (this.currentOrder?.columnIndex === index) {
            if (this.currentOrder?.direction === DIRECTION.NONE) {
              return DIRECTION.DOWN;
            }

            if (this.currentOrder?.direction === DIRECTION.DOWN) {
              return DIRECTION.UP;
            }

            if (this.currentOrder?.direction === DIRECTION.UP) {
              return DIRECTION.NONE;
            }
          }

          return DIRECTION.DOWN;
        };

        const setIcon = (span: HTMLSpanElement, direction: DIRECTION) => {
          if (direction === DIRECTION.UP) {
            span.innerHTML = 'arrow_drop_down';
          }

          if (direction === DIRECTION.DOWN) {
            span.innerHTML = 'arrow_drop_up';
          }

          if (direction === DIRECTION.NONE) {
            span.innerHTML = 'swap_vert';
          }
        };

        $ordenableToolClone.querySelector('button')?.addEventListener('click', (event: Event) => {
          const button = event.currentTarget as HTMLButtonElement;
          cleanAllOrders();

          this.currentOrder = {
            columnIndex: index,
            direction: getDirection(),
          };

          if (this.currentOrder.direction !== '') {
            button.classList.add(this.activeElementsClass);
          }

          setIcon(button.querySelector('span') as HTMLSpanElement, this.currentOrder.direction);
          this.ordem(index, this.currentOrder.direction);
        });

        column.classList.add('ordenable-container');
        $ordenableToolClone.append(column.innerHTML);

        column.innerHTML = '';
        column.append($ordenableToolClone);
      }
    });
  }

  ordem(columnIndex: number, direction: DIRECTION) {
    const allData = this.allData$.value;
    const allDataCopy = [...allData];

    this.currentCollumn = columnIndex;
    this.currentDirection = direction;

    if (direction !== DIRECTION.NONE) {
      const comparationType = this.configs?.collumnConfig?.[columnIndex]?.type || 'text';

      const compareByText = (a: any, b: any) => {
        const valueA = a[Object.keys(a)[columnIndex]].toString().toLowerCase();
        const valueB = b[Object.keys(b)[columnIndex]].toString().toLowerCase();

        if (direction === DIRECTION.UP) {
          return valueB.localeCompare(valueA);
        } else {
          return valueA.localeCompare(valueB);
        }
      };

      const compareByNumber = (a: any, b: any) => {
        const valueA = parseInt(a[Object.keys(a)[columnIndex]]);
        const valueB = parseInt(b[Object.keys(b)[columnIndex]]);

        if (direction === DIRECTION.UP) {
          return valueB - valueA;
        } else {
          return valueA - valueB;
        }
      };

      const compareByDate = (a: any, b: any) => {
        const rawA = a[Object.keys(a)[columnIndex]];
        const rawB = b[Object.keys(b)[columnIndex]];

        const valueA = rawA ? new Date(rawA) : null;
        const valueB = rawB ? new Date(rawB) : null;

        const isValidDate = (date: any) => date instanceof Date && !isNaN(date.getTime());

        const validA = isValidDate(valueA);
        const validB = isValidDate(valueB);

        if (!validA && !validB) {
          return 0;
        }

        if (!validA) {
          return direction === DIRECTION.UP ? 1 : -1;
        }

        if (!validB) {
          return direction === DIRECTION.UP ? -1 : 1;
        }

        return direction === DIRECTION.UP
          ? valueB!.getTime() - valueA!.getTime()
          : valueA!.getTime() - valueB!.getTime();
      };

      const compareByBoolean = (a: any, b: any) => {
        const valueA = a[Object.keys(a)[columnIndex]] === 'true';
        const valueB = b[Object.keys(b)[columnIndex]] === 'true';

        if (direction === DIRECTION.UP) {
          return valueA === valueB ? 0 : valueA ? 1 : -1;
        } else {
          return valueA === valueB ? 0 : valueA ? -1 : 1;
        }
      };

      if (comparationType === 'text') {
        allDataCopy.sort(compareByText);
      }

      if (comparationType === 'number') {
        allDataCopy.sort(compareByNumber);
      }

      if (comparationType === 'date') {
        allDataCopy.sort(compareByDate);
      }

      if (comparationType === 'boolean') {
        allDataCopy.sort(compareByBoolean);
      }
    }

    this.paginateData(allDataCopy);
  }

  getPaginationTotalLabel(pagination: PaginationModel) {
    const total = pagination?.totalResults || 0;
    const currentPage = pagination?.currentPage || 0;
    const pageSize = pagination?.pageSize || 20;

    const start = currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, total);

    return this.translationService.translate('category.search.qtyLabel', { start, end, total });
  }
}
